import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase, getUserCompanyId } from '../lib/supabase'

const AGENDA = [
  { id: 'checkin',    label: 'Check in',         minutes: 5,  hint: 'How is everyone showing up today?' },
  { id: 'scorecard',  label: 'Scorecard review',  minutes: 5,  hint: 'Review the numbers. What\'s on track, off track?' },
  { id: 'priorities', label: 'Priority review',   minutes: 5,  hint: 'Review 90-day rocks. On track or off track?' },
  { id: 'headlines',  label: 'Headlines',         minutes: 5,  hint: 'Customer news · Employee news' },
  { id: 'todos',      label: 'To-do list',        minutes: 5,  hint: 'Review action items from last week. Done or not done?' },
  { id: 'issues',     label: 'Issues',            minutes: 60, hint: 'Identify · Discuss · Solve' },
  { id: 'checkout',   label: 'Check out',         minutes: 5,  hint: 'Recap to-dos · Cascading messages · Rating (1–10)' },
]

const TOTAL_MINUTES = AGENDA.reduce((s, a) => s + a.minutes, 0)

function getMeetingKey() {
  const d = new Date()
  d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1))
  return d.toISOString().split('T')[0]
}

function formatTime(seconds) {
  const m = Math.floor(Math.abs(seconds) / 60)
  const s = Math.abs(seconds) % 60
  return `${seconds < 0 ? '-' : ''}${m}:${s.toString().padStart(2, '0')}`
}

function getCurrentQuarter() {
  const now = new Date()
  return `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`
}

function getPrevWeekKey(currentKey) {
  const d = new Date(currentKey)
  d.setDate(d.getDate() - 7)
  return d.toISOString().split('T')[0]
}

function getWeekOptions() {
  const options = []
  for (let i = 0; i < 8; i++) {
    const d = new Date()
    d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1) - i * 7)
    const key = d.toISOString().split('T')[0]
    const label = i === 0 ? 'This week' : `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    options.push({ key, label })
  }
  return options
}

let todoCounter = Date.now()

export default function Weekly({ previewUserId }) {
  const { user: authUser } = useAuth()
  const user = previewUserId ? { ...authUser, id: previewUserId } : authUser

  const [meetings, setMeetings] = useState({})
  const [weekKey, setWeekKey] = useState(getMeetingKey())
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('saved')
  const [dbReady, setDbReady] = useState(true)
  const [activeSection, setActiveSection] = useState(null)
  const [timerSeconds, setTimerSeconds] = useState(0)
  const [timerRunning, setTimerRunning] = useState(false)
  const [meetingStarted, setMeetingStarted] = useState(false)
  const [scorecardData, setScorecardData] = useState(null)
  const [prioritiesData, setPrioritiesData] = useState(null)
  const timerRef = useRef(null)
  const [companyId, setCompanyId] = useState(previewUserId || null)
  const [companyIdResolved, setCompanyIdResolved] = useState(!!previewUserId)

  // Mobile detection
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    if (previewUserId) { setCompanyId(previewUserId); setCompanyIdResolved(true); return }
    if (!authUser?.email) return
    let alive = true
    getUserCompanyId(authUser.email).then(cid => { if (!alive) return; setCompanyId(cid); setCompanyIdResolved(true) })
    return () => { alive = false }
  }, [authUser?.email, previewUserId])

  useEffect(() => {
    async function load() {
      if (!user || !companyIdResolved) return
      if (!companyId) { setLoading(false); return }
      try {
        const [{ data: wData }, { data: scData }, { data: prData }] = await Promise.all([
          supabase.from('weekly_meetings').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(10),
          supabase.from('scorecards').select('scorecard_data').eq('company_id', companyId).maybeSingle(),
          supabase.from('priorities').select('priorities_data').eq('company_id', companyId).maybeSingle(),
        ])
        const map = {}
        ;(wData || []).forEach(r => { map[r.week_key] = r.meeting_data })
        setMeetings(map)
        setScorecardData(scData?.scorecard_data || null)
        setPrioritiesData(prData?.priorities_data || null)
      } catch {
        setDbReady(false)
      }
      setLoading(false)
    }
    load()
  }, [user?.id, companyId, companyIdResolved])

  useEffect(() => {
    if (!meetings || loading || !dbReady) return
    if (previewUserId) return // read-only in admin preview
    if (!companyId) return
    const meeting = meetings[weekKey]
    if (!meeting) return
    setSaveStatus('unsaved')
    const t = setTimeout(async () => {
      setSaveStatus('saving')
      await supabase.from('weekly_meetings').upsert(
        { user_id: user.id, company_id: companyId, week_key: weekKey, meeting_data: meeting, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,week_key' }
      )
      setSaveStatus('saved')
    }, 1500)
    return () => clearTimeout(t)
  }, [meetings])

  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => setTimerSeconds(s => s + 1), 1000)
    } else {
      clearInterval(timerRef.current)
    }
    return () => clearInterval(timerRef.current)
  }, [timerRunning])

  function getMeeting() {
    return meetings[weekKey] || { sections: {} }
  }

  function updateSection(sectionId, field, val) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      if (!meeting.sections[sectionId]) meeting.sections[sectionId] = {}
      meeting.sections[sectionId][field] = val
      next[weekKey] = meeting
      return next
    })
  }

  function addTodo(sectionId) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      if (!meeting.sections[sectionId]) meeting.sections[sectionId] = {}
      if (!meeting.sections[sectionId].todos) meeting.sections[sectionId].todos = []
      meeting.sections[sectionId].todos.push({ id: ++todoCounter, text: '', owner: '', done: false })
      next[weekKey] = meeting
      return next
    })
  }

  function updateTodo(sectionId, todoId, field, val, targetWeekKey) {
    setMeetings(prev => {
      const next = { ...prev }
      const key = targetWeekKey || weekKey
      const meeting = JSON.parse(JSON.stringify(next[key] || { sections: {} }))
      const todo = meeting.sections[sectionId]?.todos?.find(t => t.id === todoId)
      if (todo) todo[field] = val
      next[key] = meeting
      return next
    })
  }

  function deleteTodo(sectionId, todoId) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      if (meeting.sections[sectionId]?.todos) {
        meeting.sections[sectionId].todos = meeting.sections[sectionId].todos.filter(t => t.id !== todoId)
      }
      next[weekKey] = meeting
      return next
    })
  }

  function addIssue(sectionId) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      if (!meeting.sections[sectionId]) meeting.sections[sectionId] = {}
      if (!meeting.sections[sectionId].issues) meeting.sections[sectionId].issues = []
      meeting.sections[sectionId].issues.push({ id: ++todoCounter, text: '', solved: false })
      next[weekKey] = meeting
      return next
    })
  }

  function updateIssue(sectionId, issueId, field, val) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      const issue = meeting.sections[sectionId]?.issues?.find(i => i.id === issueId)
      if (issue) issue[field] = val
      next[weekKey] = meeting
      return next
    })
  }

  function deleteIssue(sectionId, issueId) {
    setMeetings(prev => {
      const next = { ...prev }
      const meeting = JSON.parse(JSON.stringify(next[weekKey] || { sections: {} }))
      if (meeting.sections[sectionId]?.issues) {
        meeting.sections[sectionId].issues = meeting.sections[sectionId].issues.filter(i => i.id !== issueId)
      }
      next[weekKey] = meeting
      return next
    })
  }

  async function updateRockStatus(rockId, newStatus, isCompanyRock, personId) {
    if (!prioritiesData) return
    const next = JSON.parse(JSON.stringify(prioritiesData))
    const q = getCurrentQuarter()
    if (!next.quarters?.[q]) return
    if (isCompanyRock) {
      const rock = next.quarters[q].companyRocks?.find(r => String(r.id) === String(rockId))
      if (rock) rock.status = newStatus
    } else {
      const person = next.quarters[q].individuals?.find(p => String(p.id) === String(personId))
      const rock = person?.rocks?.find(r => String(r.id) === String(rockId))
      if (rock) rock.status = newStatus
    }
    await supabase.from('priorities').upsert(
      { user_id: user.id, company_id: companyId, priorities_data: next, updated_at: new Date().toISOString() },
      { onConflict: 'company_id' }
    )
    setPrioritiesData(next)
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#888' }}>Loading…</div>
    </div>
  )

  const meeting = getMeeting()
  const weekOptions = getWeekOptions()
  const sectionsDone = AGENDA.filter(a => meeting.sections[a.id]?.done).length

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar — desktop only */}
      {!isMobile && (
      <div style={{ width: 220, borderRight: '1px solid #EBE7DE', background: '#FAFAF8', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'auto' }}>
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid #EBE7DE' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: '#888780', marginBottom: 8 }}>The Weekly</div>
          <select value={weekKey} onChange={e => setWeekKey(e.target.value)} style={{ width: '100%', fontSize: 12, border: '1px solid #D0CDC7', borderRadius: 6, padding: '5px 8px', background: '#fff', color: '#2C2C2A' }}>
            {weekOptions.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #EBE7DE' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888780', marginBottom: 6 }}>
            <span>Progress</span><span>{sectionsDone}/{AGENDA.length}</span>
          </div>
          <div style={{ height: 4, background: '#EBE7DE', borderRadius: 2 }}>
            <div style={{ height: 4, background: '#3B6D11', borderRadius: 2, width: `${Math.round((sectionsDone / AGENDA.length) * 100)}%`, transition: 'width .3s' }} />
          </div>
        </div>
        <div style={{ flex: 1, padding: '8px' }}>
          {AGENDA.map((item, i) => {
            const done = meeting.sections[item.id]?.done
            const active = activeSection === item.id
            return (
              <button key={item.id}
                onClick={() => {
                  document.getElementById(`section-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  setActiveSection(item.id)
                }}
                style={{ width: '100%', textAlign: 'left', border: 'none', borderRadius: 6, padding: '8px 10px', marginBottom: 2, cursor: 'pointer', background: active ? '#1E3320' : done ? '#F0FDF4' : 'transparent', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `1.5px solid ${done ? '#22C55E' : active ? 'rgba(255,255,255,0.4)' : '#D0CDC7'}`, background: done ? '#22C55E' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff' }}>
                  {done ? '✓' : <span style={{ fontSize: 9, color: active ? 'rgba(255,255,255,0.5)' : '#888' }}>{i + 1}</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: active ? '#fff' : done ? '#166534' : '#2C2C2A', lineHeight: 1.2 }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: active ? 'rgba(255,255,255,0.5)' : '#aaa' }}>{item.minutes} min</div>
                </div>
              </button>
            )
          })}
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #EBE7DE', fontSize: 11, color: '#888780', display: 'flex', justifyContent: 'space-between' }}>
          <span>Total</span><span style={{ fontWeight: 600, color: '#2C2C2A' }}>{TOTAL_MINUTES} min</span>
        </div>
      </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '12px 16px' : '20px 32px',
          borderBottom: '1px solid #EBE7DE',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: isMobile ? 8 : 12,
          position: 'sticky', top: 0, background: '#fff', zIndex: 10,
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h1 style={{ fontSize: isMobile ? 17 : 20, fontWeight: 600, color: '#2C2C2A', marginBottom: 2 }}>The Weekly</h1>
            {!isMobile && <div style={{ fontSize: 12, color: '#888780' }}>{weekOptions.find(w => w.key === weekKey)?.label}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>
            {meetingStarted && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: timerRunning ? '#1E3320' : '#F1EFE8', borderRadius: 8, padding: '6px 14px' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: timerRunning ? '#22C55E' : '#D0CDC7' }} />
                <span style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: timerRunning ? '#fff' : '#2C2C2A', fontFamily: 'monospace' }}>{formatTime(timerSeconds)}</span>
                <button onClick={() => setTimerRunning(r => !r)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: timerRunning ? 'rgba(255,255,255,0.7)' : '#888' }}>{timerRunning ? 'Pause' : 'Resume'}</button>
              </div>
            )}
            <span style={{ fontSize: 11, color: saveStatus === 'saved' ? '#3B6D11' : '#888' }}>
              {saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'saving' ? '↑ Saving…' : '● Unsaved'}
            </span>
            {!meetingStarted
              ? <button onClick={() => { setMeetingStarted(true); setTimerRunning(true); setActiveSection('checkin') }} style={{ fontSize: 12, padding: isMobile ? '8px 14px' : '8px 18px', borderRadius: 7, border: 'none', background: '#1E3320', color: '#fff', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{isMobile ? 'Start' : 'Start meeting'}</button>
              : <button onClick={() => { setMeetingStarted(false); setTimerRunning(false); setTimerSeconds(0) }} style={{ fontSize: 12, padding: isMobile ? '8px 14px' : '8px 18px', borderRadius: 7, border: '1px solid #D0CDC7', background: 'transparent', color: '#888', cursor: 'pointer', whiteSpace: 'nowrap' }}>{isMobile ? 'End' : 'End meeting'}</button>
            }
          </div>
        </div>

        {/* Mobile-only: week picker + progress */}
        {isMobile && (
          <div style={{ padding: '12px 16px', background: '#FAFAF8', borderBottom: '1px solid #EBE7DE', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <select value={weekKey} onChange={e => setWeekKey(e.target.value)}
              style={{ width: '100%', fontSize: 14, border: '1px solid #D0CDC7', borderRadius: 6, padding: '8px 10px', background: '#fff', color: '#2C2C2A' }}>
              {weekOptions.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
            </select>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888780', marginBottom: 4 }}>
                <span>Progress · {sectionsDone}/{AGENDA.length} done</span>
                <span>{TOTAL_MINUTES} min total</span>
              </div>
              <div style={{ height: 4, background: '#EBE7DE', borderRadius: 2 }}>
                <div style={{ height: 4, background: '#3B6D11', borderRadius: 2, width: `${Math.round((sectionsDone / AGENDA.length) * 100)}%`, transition: 'width .3s' }} />
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: isMobile ? '0 14px 80px' : '0 32px 80px' }}>
          {AGENDA.map((item, i) => {
            const section = meeting.sections[item.id] || {}
            const isActive = activeSection === item.id
            const isDone = section.done

            return (
              <div key={item.id} id={`section-${item.id}`} style={{ marginTop: 24, borderRadius: 10, border: `1.5px solid ${isActive ? '#1E3320' : isDone ? '#86EFAC' : '#EBE7DE'}`, background: '#fff', overflow: 'hidden', transition: 'border-color .2s' }}>
                
                {/* Header — clicking this activates section */}
                <div
                  onClick={() => setActiveSection(isActive ? null : item.id)}
                  style={{ padding: isMobile ? '12px 14px' : '14px 20px', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 10 : 12, borderBottom: isActive ? '1px solid #E0DCD2' : isDone ? '1px solid #D1FAE5' : 'none', background: isDone ? '#F0FDF4' : isActive ? '#1E3320' : '#fff', cursor: 'pointer', userSelect: 'none', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: isDone ? '#22C55E' : isActive ? 'rgba(255,255,255,0.15)' : '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: isDone ? '#fff' : isActive ? '#fff' : '#888', flexShrink: 0 }}>
                    {isDone ? '✓' : i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: isActive ? '#fff' : isDone ? '#166534' : '#2C2C2A' }}>{item.label}</div>
                      <span style={{ fontSize: 11, color: isActive ? 'rgba(255,255,255,0.5)' : '#aaa', flexShrink: 0 }}>{item.minutes} min</span>
                    </div>
                    <div style={{ fontSize: 12, color: isActive ? 'rgba(255,255,255,0.55)' : '#aaa', marginTop: 1 }}>{item.hint}</div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); updateSection(item.id, 'done', !isDone) }}
                    style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: `1px solid ${isDone ? '#86EFAC' : isActive ? 'rgba(255,255,255,0.3)' : '#D0CDC7'}`, background: isDone ? '#22C55E' : 'transparent', color: isDone ? '#fff' : isActive ? 'rgba(255,255,255,0.8)' : '#888', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', alignSelf: isMobile ? 'flex-start' : 'center', marginLeft: isMobile ? 38 : 0, marginTop: isMobile ? 2 : 0 }}
                  >{isDone ? '✓ Done' : 'Mark done'}</button>
                </div>

                {/* Body — completely separate from header, no click conflicts */}
                {isActive && (
                  <div style={{ padding: isMobile ? '16px 14px' : '20px' }}>

                    {/* SCORECARD */}
                    {item.id === 'scorecard' && scorecardData?.metrics?.length > 0 && (
                      <div style={{ border: '1px solid #EBE7DE', borderRadius: 6, overflow: 'hidden', marginBottom: 4 }}>
                       <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#F1EFE8' }}>
                              <th style={thS}>Metric</th><th style={thS}>Owner</th><th style={{ ...thS, textAlign: 'center' }}>Goal</th><th style={{ ...thS, textAlign: 'center' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scorecardData.metrics.map((m, mi) => {
                              const lk = Object.keys(m.entries || {}).sort().reverse()[0]
                              const latest = lk ? m.entries[lk] : {}
                              const sc = { green: { bg: '#F0FDF4', color: '#166534' }, yellow: { bg: '#FEFCE8', color: '#854D0E' }, red: { bg: '#FEF2F2', color: '#991B1B' } }[latest.status || 'green']
                              return (
                                <tr key={m.id} style={{ background: mi % 2 === 0 ? '#fff' : '#FAFAF8', borderTop: '1px solid #EBE7DE' }}>
                                  <td style={tdS}>{m.name}</td>
                                  <td style={{ ...tdS, color: '#888780' }}>{m.owner || '—'}</td>
                                  <td style={{ ...tdS, textAlign: 'center' }}>{m.goal || '—'}</td>
                                  <td style={{ ...tdS, textAlign: 'center' }}>
                                    <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: sc.bg, color: sc.color }}>
                                      {latest.status ? latest.status.charAt(0).toUpperCase() + latest.status.slice(1) : 'No data'}
                                    </span>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                       </div>
                      </div>
                    )}

                    {/* PRIORITIES */}
                    {item.id === 'priorities' && (() => {
                      const q = getCurrentQuarter()
                      const qData = prioritiesData?.quarters?.[q]
                      const allRocks = [
                        ...(qData?.companyRocks || []).map(r => ({ ...r, isCompany: true, personId: null, person: r.owner || 'Unassigned' })),
                        ...(qData?.individuals || []).flatMap(p => (p.rocks || []).map(r => ({ ...r, isCompany: false, personId: p.id, person: p.name })))
                      ]
                      if (!allRocks.length) return <div style={{ fontSize: 13, color: '#aaa', padding: '12px 0' }}>No priorities set for {q} yet.</div>
                      const statusColors = { on_track: { bg: '#F0FDF4', color: '#166534' }, off_track: { bg: '#FEF2F2', color: '#991B1B' }, complete: { bg: '#EFF6FF', color: '#1E3A8A' } }
                      return (
                        <div style={{ border: '1px solid #EBE7DE', borderRadius: 6, overflow: 'hidden', marginBottom: 4 }}>
                         <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <table style={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: '#F1EFE8' }}>
                                <th style={thS}>Rock</th><th style={thS}>Owner</th><th style={{ ...thS, textAlign: 'center' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allRocks.map((r, ri) => {
                                const sc = statusColors[r.status || 'on_track']
                                return (
                                  <tr key={r.id} style={{ background: ri % 2 === 0 ? '#fff' : '#FAFAF8', borderTop: '1px solid #EBE7DE' }}>
                                    <td style={{ ...tdS, textDecoration: r.status === 'complete' ? 'line-through' : 'none', color: r.status === 'complete' ? '#aaa' : '#2C2C2A' }}>{r.title}</td>
                                    <td style={{ ...tdS, color: '#888780' }}>{r.person}</td>
                                    <td style={{ ...tdS, textAlign: 'center' }}>
                                      <select value={r.status || 'on_track'} onChange={e => updateRockStatus(r.id, e.target.value, r.isCompany, r.personId)}
                                        style={{ fontSize: 11, border: `1px solid ${sc.bg === '#F0FDF4' ? '#86EFAC' : sc.bg === '#FEF2F2' ? '#FCA5A5' : '#93C5FD'}`, borderRadius: 4, padding: '2px 6px', background: sc.bg, color: sc.color, cursor: 'pointer' }}>
                                        <option value="on_track">On track</option>
                                        <option value="off_track">Off track</option>
                                        <option value="complete">Complete</option>
                                      </select>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                         </div>
                        </div>
                      )
                    })()}

                    {/* HEADLINES — notes */}
                    {item.id === 'headlines' && (
                      <textarea value={section.notes || ''} onChange={e => updateSection(item.id, 'notes', e.target.value)}
                        placeholder="Customer news · Employee news…"
                        style={{ width: '100%', fontSize: 13, border: '1px solid #EBE7DE', borderRadius: 6, padding: '10px 12px', outline: 'none', resize: 'vertical', minHeight: 100, lineHeight: 1.7, color: '#2C2C2A', background: '#FAFAF8', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    )}

                    {/* TO-DO LIST — pulls prior week's checkout items */}
                    {item.id === 'todos' && (() => {
                      const prevKey = getPrevWeekKey(weekKey)
                      const prevCheckout = meetings[prevKey]?.sections?.checkout?.todos || []
                      return (
                        <div>
                          {prevCheckout.length > 0 ? (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 10 }}>
                                From last week's check out
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {prevCheckout.map(todo => (
                                  <div key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: todo.done ? '#F0FDF4' : '#F9F7F4', border: `1px solid ${todo.done ? '#86EFAC' : '#EBE7DE'}`, borderRadius: 6, padding: '8px 10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                                    <input type="checkbox" checked={!!todo.done} onChange={e => updateTodo('checkout', todo.id, 'done', e.target.checked, prevKey)} style={{ flexShrink: 0, cursor: 'pointer', width: 18, height: 18 }} />
                                    <span style={{ flex: '1 1 60%', fontSize: 13, minWidth: 120, textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? '#888' : '#2C2C2A' }}>{todo.text || '(no description)'}</span>
                                    {todo.owner && <span style={{ fontSize: 11, color: '#888780', background: '#fff', border: '1px solid #EBE7DE', borderRadius: 4, padding: '2px 8px', flexShrink: 0 }}>{todo.owner}</span>}
                                    <span style={{ fontSize: 11, fontWeight: 500, color: todo.done ? '#166534' : '#991B1B', flexShrink: 0 }}>{todo.done ? '✓ Done' : 'Not done'}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#aaa', padding: '8px 0' }}>
                              No action items found from last week's check out.
                            </div>
                          )}
                        </div>
                      )
                    })()}

                    {/* CHECKOUT — action items for this week */}
                    {item.id === 'checkout' && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 10 }}>Action items</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                          {(section.todos || []).map(todo => (
                            <div key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F9F7F4', border: '1px solid #EBE7DE', borderRadius: 6, padding: '7px 10px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                              <input type="checkbox" checked={!!todo.done} onChange={e => updateTodo(item.id, todo.id, 'done', e.target.checked)} style={{ flexShrink: 0, cursor: 'pointer', width: 18, height: 18 }} />
                              <input value={todo.text || ''} onChange={e => updateTodo(item.id, todo.id, 'text', e.target.value)} placeholder="Action item…"
                                style={{ flex: '1 1 60%', fontSize: 14, minWidth: 120, border: 'none', outline: 'none', background: 'transparent', textDecoration: todo.done ? 'line-through' : 'none', color: todo.done ? '#aaa' : '#2C2C2A' }} />
                              <input value={todo.owner || ''} onChange={e => updateTodo(item.id, todo.id, 'owner', e.target.value)} placeholder="Owner"
                                style={{ width: isMobile ? '100%' : 110, fontSize: 13, border: '1px solid #EBE7DE', borderRadius: 4, padding: '6px 8px', outline: 'none', background: '#fff', color: '#4A4840', order: isMobile ? 3 : 0 }} />
                              <button onClick={() => deleteTodo(item.id, todo.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, flexShrink: 0, lineHeight: 1, padding: 4 }}>×</button>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => addTodo(item.id)} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #3B6D11', background: 'transparent', color: '#3B6D11', cursor: 'pointer', fontWeight: 500 }}>
                          + Add action item
                        </button>
                      </div>
                    )}

                    {/* ISSUES — checkbox list */}
                    {item.id === 'issues' && (
                      <div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                          {(section.issues || []).map(issue => (
                            <div key={issue.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F9F7F4', border: '1px solid #EBE7DE', borderRadius: 6, padding: '8px 12px' }}>
                              <input type="checkbox" checked={!!issue.solved} onChange={e => updateIssue(item.id, issue.id, 'solved', e.target.checked)} style={{ flexShrink: 0, cursor: 'pointer', width: 18, height: 18 }} />
                              <input value={issue.text || ''} onChange={e => updateIssue(item.id, issue.id, 'text', e.target.value)} placeholder="Issue…"
                                style={{ flex: 1, fontSize: 14, border: 'none', outline: 'none', background: 'transparent', textDecoration: issue.solved ? 'line-through' : 'none', color: issue.solved ? '#aaa' : '#2C2C2A' }} />
                              <button onClick={() => deleteIssue(item.id, issue.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, flexShrink: 0, lineHeight: 1, padding: 4 }}>×</button>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => addIssue(item.id)} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #3B6D11', background: 'transparent', color: '#3B6D11', cursor: 'pointer', fontWeight: 500 }}>
                          + Add issue
                        </button>
                      </div>
                    )}

                    {/* CHECK IN — no body needed, just mark done */}
                    {item.id === 'checkin' && (
                      <div style={{ fontSize: 13, color: '#888780', padding: '4px 0' }}>Go around the room — how is everyone showing up today?</div>
                    )}

                    {/* CHECKOUT — rating */}
                    {item.id === 'checkout' && (
                      <div style={{ marginTop: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 10 }}>Meeting rating (1–10)</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <button key={n} onClick={() => updateSection(item.id, 'rating', n)}
                              style={{ width: 36, height: 36, borderRadius: 6, border: `1.5px solid ${section.rating === n ? '#1E3320' : '#EBE7DE'}`, background: section.rating === n ? '#1E3320' : '#fff', color: section.rating === n ? '#fff' : '#4A4840', fontSize: 13, fontWeight: section.rating === n ? 600 : 400, cursor: 'pointer' }}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const thS = { padding: '7px 12px', textAlign: 'left', fontWeight: 500, color: '#888780', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em' }
const tdS = { padding: '8px 12px', fontWeight: 400, color: '#2C2C2A' }
